/**
 * «Юниверситет» (28.09.2026) — учебная мини-RPG в кабинете оператора. Общее ядро для сервера
 * (routes/university.ts: вопросы дня, слияние прогресса) и клиента (screens/op/uni: движок).
 * Устройство игры — pack.json (scripts/build-uni-pack.mjs из «Психушка — база знаний»):
 * комнаты по порядку path, квесты со ссылками на задания по постоянным id, вопросы дня, ачивки.
 * Здесь только чистые функции: без сети, без React, без хранения.
 */

export interface UniItem {
  q?: string; title?: string; type?: "input" | "multi"; options?: string[];
  answer?: number | number[] | boolean | string | string[]; steps?: string[];
  placeholder?: string; img?: string; video?: string; explain?: string;
}
export interface UniQuest {
  id: string; kind: string; xp: number; title?: string; items?: string[];
  by_class?: Record<string, { title: string; items: string[] }>; boss?: boolean; pass_percent?: number; note?: string;
}
export interface UniRoom {
  id: string; name: string; sub: string; map: [number, number]; cover: string; scene?: string; track?: string;
  boss?: boolean; secret?: boolean; locked_text?: string; quests: UniQuest[];
}
export interface UniAch { id: string; title: string; sign: string; when: Record<string, unknown> }
export interface UniTrack { title: string; artist?: string; file: string }
export interface UniPack {
  pack: string; title: string; version: number; level_xp: number; pass_percent: number;
  classes: { id: string; title: string }[]; path: string[]; rooms: UniRoom[];
  daily: { count: number; xp: number; day_tz?: string; pool: string[] };
  achievements: UniAch[]; items: Record<string, UniItem>; tracks: UniTrack[]; files?: string[];
  /** бейджи грейдов: «I»…«VII» → путь в пакете (badges/grade-I.webp) */
  badges?: Record<string, string>;
}
export interface UniQuestRec { best: number; of: number; perfect?: boolean; done_at?: string }
export interface UniProgress {
  v: 1; cls?: string; cls_at?: string; xp?: number;
  quests: Record<string, UniQuestRec>;
  daily: Record<string, Record<string, 0 | 1>>;
  tracks: string[];
  ach: Record<string, string>;
}

export const emptyProgress = (): UniProgress => ({ v: 1, quests: {}, daily: {}, tracks: [], ach: {} });

/** Вид задания — как в макете: steps → «по порядку»; иначе type; иначе по answer. */
export type UniKind = "steps" | "input" | "multi" | "bool" | "single";
export function itemKind(it: UniItem): UniKind {
  if (Array.isArray(it.steps)) return "steps";
  if (it.type === "input") return "input";
  if (it.type === "multi" || Array.isArray(it.answer)) return "multi";
  if (typeof it.answer === "boolean") return "bool";
  return "single";
}

/** Сравнение ввода: без регистра, пробелов, точек, запятых, дефисов; «ё» = «е». */
export const normAnswer = (s: unknown) => String(s ?? "").toLowerCase().replace(/[\s.,-]/g, "").replace(/ё/g, "е");
export const inputOk = (it: UniItem, v: string) => ([] as unknown[]).concat(it.answer ?? []).map(normAnswer).includes(normAnswer(v));

/** Задания квеста для класса: by_class — свой набор; иначе общий. */
export function questItems(q: UniQuest, cls: string | undefined, pack: UniPack): { title: string; items: string[] } {
  if (q.by_class) {
    const c = q.by_class[cls ?? ""] ?? q.by_class[pack.classes[0]?.id ?? ""] ?? Object.values(q.by_class)[0];
    return { title: c?.title ?? q.title ?? q.id, items: c?.items ?? [] };
  }
  return { title: q.title ?? q.id, items: q.items ?? [] };
}

/** Зачёт — целыми числами: got*100 ≥ p*n (у квеста свой pass_percent важнее общего). */
export const passPercent = (q: UniQuest, pack: UniPack) => q.pass_percent ?? pack.pass_percent;
export const questPassed = (got: number, n: number, pct: number) => n > 0 && got * 100 >= pct * n;

export const questDone = (p: UniProgress, qid: string) => !!p.quests[qid]?.done_at;
export const roomDone = (p: UniProgress, r: UniRoom) => r.quests.length > 0 && r.quests.every((q) => questDone(p, q.id));

export type RoomState = "done" | "cur" | "lock" | "secret";
/** Состояния комнат: path по порядку (следующая — когда пройдена предыдущая; комната, где уже
 *  есть пройденный квест, не закрывается обратно), секретная — когда пройдены все из path. */
export function roomStates(pack: UniPack, p: UniProgress): Record<string, RoomState> {
  const out: Record<string, RoomState> = {};
  const byId = new Map(pack.rooms.map((r) => [r.id, r]));
  let open: boolean = true;
  for (const id of pack.path) {
    const r = byId.get(id); if (!r) continue;
    const sticky = r.quests.some((q) => questDone(p, q.id));
    const isOpen: boolean = open || sticky;
    out[id] = roomDone(p, r) && isOpen ? "done" : isOpen ? "cur" : "lock";
    open = isOpen && roomDone(p, r);
  }
  const allPath = pack.path.every((id) => out[id] === "done");
  for (const r of pack.rooms) {
    if (out[r.id]) continue;
    out[r.id] = allPath ? (roomDone(p, r) ? "done" : "cur") : (r.secret ? "secret" : "lock");
  }
  return out;
}
/** «Сейчас: …» — первая открытая непройденная комната пути, потом прочие; null — всё пройдено. */
export function currentRoom(pack: UniPack, st: Record<string, RoomState>): UniRoom | null {
  const order = [...pack.path, ...pack.rooms.map((r) => r.id).filter((id) => !pack.path.includes(id))];
  const id = order.find((x) => st[x] === "cur");
  return id ? pack.rooms.find((r) => r.id === id) ?? null : null;
}

export const dailyCorrect = (p: UniProgress) => Object.values(p.daily).reduce((s, d) => s + Object.values(d).filter((v) => v === 1).length, 0);
/** Опыт: квесты (один раз за квест) + вопросы дня (за каждый верный). */
export function xpOf(pack: UniPack, p: UniProgress): number {
  let xp = 0;
  for (const r of pack.rooms) for (const q of r.quests) if (questDone(p, q.id)) xp += q.xp;
  return xp + dailyCorrect(p) * pack.daily.xp;
}

/** Ачивки по условиям when. manual и неизвестные ключи — закрыты. «Три из трёх» — верные за день. */
export function achEarned(pack: UniPack, p: UniProgress): string[] {
  const doneN = Object.values(p.quests).filter((q) => q.done_at).length;
  const perfectN = Object.values(p.quests).filter((q) => q.perfect).length;
  const bestDay = Math.max(0, ...Object.values(p.daily).map((d) => Object.values(d).filter((v) => v === 1).length));
  return pack.achievements.filter((a) => {
    const w = a.when; const k = Object.keys(w)[0];
    if (k === "quests_done") return doneN >= Number(w[k]);
    if (k === "quest") return questDone(p, String(w[k]));
    if (k === "distinct_tracks") return new Set(p.tracks).size >= Number(w[k]);
    if (k === "daily_in_a_day") return bestDay >= Number(w[k]);
    if (k === "quest_without_mistakes") return perfectN >= Number(w[k]);
    return false;
  }).map((a) => a.id);
}

/** Дата «сегодня» по UTC+5 (Оренбург) — у вопросов дня одна на всех. */
export const orenDate = (now = Date.now()) => new Date(now + 5 * 3_600_000).toISOString().slice(0, 10);

/** Вопросы дня: count разных id из pool, детерминированно по паку и дате (одинаково у всех). */
export function dailyPick(pack: Pick<UniPack, "pack" | "daily">, date: string): string[] {
  let h = 2166136261;
  for (const ch of `${pack.pack}:${date}`) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619) >>> 0; }
  const rnd = () => { h = (h + 0x6d2b79f5) >>> 0; let t = h; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  const a = [...pack.daily.pool];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [a[i], a[j]] = [a[j]!, a[i]!]; }
  return a.slice(0, Math.min(pack.daily.count, a.length));
}

/** Слияние прогресса (сервер + черновик устройства): ничего не теряется и не удваивается —
 *  лучший результат max, пройдено — раньшая отметка, ответ дня — первый, треки — объединение. */
export function mergeProgress(a: UniProgress, b: UniProgress): UniProgress {
  const quests: Record<string, UniQuestRec> = { ...a.quests };
  for (const [id, r] of Object.entries(b.quests ?? {})) {
    const x = quests[id];
    if (!x) { quests[id] = { ...r }; continue; }
    const done = [x.done_at, r.done_at].filter(Boolean).sort()[0];
    quests[id] = { best: Math.max(x.best, r.best), of: r.best > x.best ? r.of : x.of, ...(x.perfect || r.perfect ? { perfect: true } : {}), ...(done ? { done_at: done } : {}) };
  }
  const daily: UniProgress["daily"] = {};
  for (const d of new Set([...Object.keys(a.daily ?? {}), ...Object.keys(b.daily ?? {})])) daily[d] = { ...(b.daily?.[d] ?? {}), ...(a.daily?.[d] ?? {}) };
  const ach: Record<string, string> = { ...(b.ach ?? {}) };
  for (const [id, t] of Object.entries(a.ach ?? {})) if (!ach[id] || t < ach[id]!) ach[id] = t;
  const bNewer = (b.cls_at ?? "") >= (a.cls_at ?? "");
  return {
    v: 1, quests, daily, ach, tracks: [...new Set([...(a.tracks ?? []), ...(b.tracks ?? [])])],
    cls: (bNewer ? b.cls : a.cls) ?? a.cls ?? b.cls, cls_at: bNewer ? b.cls_at ?? a.cls_at : a.cls_at,
    xp: Math.max(a.xp ?? 0, b.xp ?? 0),
  };
}

/** Белый список учебных пакетов — настройка филиала в конфигураторе EO (branches.uni_pack). */
export const UNI_PACKS: { id: string; label: string }[] = [{ id: "", label: "Нет" }, { id: "psih", label: "Психушка" }];
export const UNI_PACK_IDS = UNI_PACKS.map((p) => p.id);

/** Бейдж грейда: 1→I … 7→VII, 8 → VII; 0/нет — без бейджа. */
export const gradeRoman = (g: number) => (g >= 1 ? ["I", "II", "III", "IV", "V", "VI", "VII"][Math.min(g, 7) - 1]! : null);
